import { Typography } from "@mui/material";
import { type GridRowSelectionModel } from "@mui/x-data-grid";
import { useState } from "react";

import { TableQueryParams } from "api/common/adapters/pagination.types";
import { PaginatedResponse } from "api/common/types/response.types";
import { useRemoveUsersFromMailListMutation } from "api/memberPortal/mailList/mailList.api";
import { MailListMember } from "api/memberPortal/mailList/mailList.types";
import ServerSidePaginatedDataGrid from "components/MUI/DataGrid/ServerSidePaginatedDataGrid";
import BulkActionSnackbar from "components/UI/Snackbars/BulkActionSnackbar";

import { mailListMemberColumns } from "./MailListDetailsMembers.columns";

interface MailListDetailsMembersTableProps {
  members: PaginatedResponse<MailListMember>;
  isLoading: boolean;
  tableParams: TableQueryParams;
  onTableParamsChange: (_queryParams: TableQueryParams) => void;
  mailListId: number;
  selectedRows: GridRowSelectionModel;
  onSelectionChange: (_newSelection: GridRowSelectionModel) => void;
}

export default function MailListDetailsMembersTable({
  members,
  isLoading,
  tableParams,
  onTableParamsChange,
  mailListId,
  selectedRows,
  onSelectionChange,
}: MailListDetailsMembersTableProps) {
  const [removeUsers, { isLoading: removeLoading }] =
    useRemoveUsersFromMailListMutation();
  const [confirmShow, setConfirmShow] = useState(false);

  const [errorText, setErrorText] = useState<string>("");

  const handleClearSelection = () => {
    onSelectionChange([]);
    setConfirmShow(false);
  };
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    onSelectionChange(newSelection);
    setConfirmShow(false);
  };

  const handleRemoveMembers = async () => {
    if (confirmShow) {
      const result = await removeUsers({
        mailListId,
        userIds: selectedRows as number[],
      });
      if ("error" in result) {
        setErrorText("An error occurred while removing members");
      } else {
        handleClearSelection();
      }
      setConfirmShow(false);
    } else {
      setConfirmShow(true);
    }
  };

  return (
    <>
      {errorText && <Typography color="error">{errorText}</Typography>}
      <ServerSidePaginatedDataGrid
        data={members}
        loading={isLoading}
        title={"Mail List Members"}
        columns={mailListMemberColumns}
        initialPaginationModel={{ page: 0, pageSize: 10 }}
        tableParams={tableParams}
        onParamsChange={onTableParamsChange}
        showToolbar={false}
        checkboxSelection
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={handleSelectionChange}
        disableZebraStripes
      />
      <BulkActionSnackbar
        selectedCount={selectedRows.length}
        isLoading={removeLoading}
        confirmShow={confirmShow}
        onConfirm={handleRemoveMembers}
        onCancel={handleClearSelection}
        actionLabel="Remove Members"
        confirmLabel={`Confirm? Removing (${selectedRows.length})`}
        color="error"
      />
    </>
  );
}
